import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@auth0/auth0-angular';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { AwsApiClient } from 'app/core/api-clients';
import { FileTypeImgs } from 'app/core/constants';
import { convertTreatmentScanToIndication } from 'app/core/helpers';
import { TreatmentScanType } from 'app/core/enums';
import { FormFile } from 'app/core/models/form-file';
import {
  DefaultTreatmentTypeFileGroup,
  PatientFileModel,
  PatientFileType,
  TreatmentTypeFile,
  TreatmentTypeFileGroup,
} from 'app/core/models';
import { ProfileStore } from 'app/core/stores';
import { CloudDriveModalService } from 'app/core/services/modals/cloud-drive-modal.service';
import { CloudDriveFile } from 'app/core/models';
import { UploadScansComponent } from '../../workflow/files-page/upload-scans';
import { PatientFileUploadState } from './patient-file-upload-state';

const SCAN_ACCEPT = '.ply,.obj';
const IMAGE_ACCEPT = '.jpeg,.jpg,.png';
const MIN_BYTES = 64;
const PATIENT_SMILING_IMAGE_PATH = 'assets/images/smile-design/front-smile.webp';

interface CloudDriveDownloadPair {
  readonly url: string;
  readonly file: CloudDriveFile;
}

@Component({
  selector: 'wl-patient-files-upload',
  standalone: true,
  imports: [CommonModule, TranslateModule, UploadScansComponent],
  templateUrl: './patient-files-upload.component.html',
  styleUrl: './patient-files-upload.component.scss',
  providers: [DialogService, CloudDriveModalService],
})
export class PatientFilesUploadComponent implements OnInit, OnChanges {
  @Input() initialFiles: PatientFileModel[] | null = null;

  @Output() readonly stateChanged = new EventEmitter<PatientFileUploadState>();

  readonly minBytes = MIN_BYTES;
  readonly categories = [
    PatientFileType.UpperScan,
    PatientFileType.BiteScan,
    PatientFileType.LowerScan,
    PatientFileType.PatientSmiling,
  ] as const;

  readonly fileConfigs: Record<PatientFileType, TreatmentTypeFile> = {
    [PatientFileType.UpperScan]: this.createFileConfig(
      PatientFileType.UpperScan,
      'general.upperScan',
      SCAN_ACCEPT,
      false
    ),
    [PatientFileType.LowerScan]: this.createFileConfig(
      PatientFileType.LowerScan,
      'general.lowerScan',
      SCAN_ACCEPT,
      false
    ),
    [PatientFileType.BiteScan]: this.createFileConfig(PatientFileType.BiteScan, 'general.biteScans', SCAN_ACCEPT, true),
    [PatientFileType.PatientSmiling]: this.createFileConfig(
      PatientFileType.PatientSmiling,
      'general.pictureOfPatientSmiling',
      IMAGE_ACCEPT,
      true
    ),
  };

  filesByCategory: Record<PatientFileType, FormFile[]> = this.createEmptyFilesByCategory();
  private readonly removedFileIds: number[] = [];

  private readonly awsClient = inject(AwsApiClient);
  private readonly authService = inject(AuthService);
  private readonly cloudDriveModal = inject(CloudDriveModalService);
  private readonly profileStore = inject(ProfileStore);

  ngOnInit(): void {
    this.emitState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFiles']) {
      this.filesByCategory = this.createEmptyFilesByCategory();
      this.removedFileIds.length = 0;

      (this.initialFiles ?? []).forEach(file => {
        this.filesByCategory[file.category].push(this.mapPatientFile(file));
      });

      this.emitState();
    }
  }

  fileSelected(files: FormFile[], category: PatientFileType): void {
    files.forEach(file => {
      file.inProgress = false;
      file.fileType = category;
    });

    this.filesByCategory[category] = [...this.filesByCategory[category], ...files];
    this.emitState();
  }

  fileRemoved(file: FormFile): void {
    const category = file.fileType as PatientFileType | undefined;

    if (category !== undefined) {
      this.filesByCategory[category] = this.filesByCategory[category].filter(item => item !== file);
    }

    if (typeof file.id === 'number') {
      this.removedFileIds.push(file.id);
    }

    this.emitState();
  }

  async openCloudDrive(category: PatientFileType): Promise<void> {
    const [user, profile] = await Promise.all([
      firstValueFrom(this.authService.user$),
      firstValueFrom(this.profileStore.profileInfo$),
    ]);

    const fileConfig = this.fileConfigs[category];
    const fileDownloadPairs = await this.cloudDriveModal.open({
      allowMultiple: fileConfig.multiple,
      doctorId: user?.sub,
      patientId: undefined,
      accept: fileConfig.accept,
      indicationTypes: convertTreatmentScanToIndication(category as TreatmentScanType),
      businessInfo: profile?.businessInfo,
      scanType: category,
      email: user?.email,
      group: fileConfig.grouping,
    });

    if (!fileDownloadPairs?.length) {
      return;
    }

    const cloudDriveFiles = fileDownloadPairs as CloudDriveDownloadPair[];
    const bufferObservables: Observable<FormFile>[] = cloudDriveFiles.map((pair: CloudDriveDownloadPair) =>
      this.getFileFromS3Url(pair.url, pair.file, category)
    );

    forkJoin(bufferObservables).subscribe((files: FormFile[]) => {
      this.filesByCategory[category].push(...files);
      this.emitState();
    });
  }

  private createFileConfig(
    category: PatientFileType,
    title: string,
    accept: string,
    multiple: boolean
  ): TreatmentTypeFile {
    const fileImage = FileTypeImgs.find(image => image.id === category);

    return {
      fileKind: 0,
      treatmentTypeId: 0,
      name: title,
      required: true,
      accept,
      title,
      type: category,
      grouping: { ...DefaultTreatmentTypeFileGroup, id: category, name: title },
      multiple,
      isPrintable: false,
      maxMegabytes: 100,
      groupBase: 0,
      isActive: true,
      order: category,
      imagePath:
        category === PatientFileType.PatientSmiling ? PATIENT_SMILING_IMAGE_PATH : (fileImage?.imagePath ?? ''),
      minBytes: MIN_BYTES,
    };
  }

  private createEmptyFilesByCategory(): Record<PatientFileType, FormFile[]> {
    return {
      [PatientFileType.UpperScan]: [],
      [PatientFileType.LowerScan]: [],
      [PatientFileType.BiteScan]: [],
      [PatientFileType.PatientSmiling]: [],
    };
  }

  private emitState(): void {
    this.stateChanged.emit({
      filesByCategory: this.filesByCategory,
      removedFileIds: [...this.removedFileIds],
      isValid: this.categories.every(category => this.filesByCategory[category].length > 0),
    });
  }

  private mapPatientFile(file: PatientFileModel): FormFile {
    return {
      id: file.id,
      s3FileId: file.s3FileId,
      progress: 0,
      inProgress: false,
      name: file.name,
      type: file.type,
      size: file.size,
      notes: '',
      file: new File([], file.name),
      createdDate: file.createdDate,
      fileType: file.category,
      fileGUID: file.fileGuid,
    };
  }

  private getFileFromS3Url(
    fileUrl: string,
    sourceFile: CloudDriveFile,
    category: PatientFileType
  ): Observable<FormFile> {
    return this.awsClient.getS3FileBuffer(fileUrl, false).pipe(
      map(response => {
        const buffer = response.body as ArrayBuffer;
        const blob = new Blob([buffer]);
        return new File([blob], sourceFile.file);
      }),
      switchMap(file => {
        const formFile: FormFile = {
          createdDate: new Date().toISOString(),
          file,
          id: null,
          inProgress: false,
          progress: 0,
          s3FileId: 0,
          name: `${sourceFile.file}${sourceFile.extension}`,
          type: sourceFile.fileType,
          size: file.size,
          notes: '',
          fileType: category,
        };

        return of(formFile);
      })
    );
  }
}
